import { Button, Link, Portal, Text, VStack } from '@chakra-ui/react';
import { useCheckoutConfigContext } from '@contexts/CheckoutConfig';
import { useDonateButtonRef } from '@contexts/DonateButtonRef';
import composeErrorMessage from '@utils/composeErrorMessage';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { When } from 'react-if';
import * as Sentry from '@sentry/react';

interface Props {
  formIsLoading: boolean;
  onComplete: () => Promise<void>;
  onError: (error: string) => void;
}

const TestPanel: FC<Props> = ({ formIsLoading, onComplete, onError }) => {
  const { ref } = useDonateButtonRef();
  const { checkout } = useCheckoutConfigContext();

  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const handleDonate = async () => {
    setLoading(true);
    try {
      await onComplete();
    } catch (err) {
      console.error(err);
      onError(t("We couldn't process your payment, please try again later"));
      Sentry.captureMessage(
        composeErrorMessage({
          code: 'CREDIT_CARD_ERROR',
          errorObject: err,
          checkout,
          campaignId: checkout.campaign.id,
          organization: checkout.organization.name,
          token: checkout.token,
        }),
      );
    }

    setLoading(false);
  };

  if (!ref?.current) return null;

  return (
    <VStack align="start" py={4} px={6} bg="orange.100">
      <Text fontWeight="bold" color="orange.700">
        {t('Test mode')}{' '}
      </Text>
      <Text>{t('Your site can not receive real donations yet.')}</Text>
      <Text>{t('You can only perform test payments.')}</Text>
      <Text>
        {t(
          'If you are the owner of the platform head to payment settings and enable your preferred gateways',
        )}
      </Text>
      <Link
        href="https://admin.riseact.org/settings/payments"
        color="blue.500"
        fontWeight="bold"
        pt={5}
      >
        {t('Go to payment settings')}
      </Link>

      <Portal containerRef={ref}>
        <When condition={checkout.enabled_payment_methods.length === 0}>
          <Button
            size="lg"
            w={{ base: 'full', lg: 'auto' }}
            alignSelf="flex-end"
            mt="auto"
            colorScheme="primary"
            isLoading={loading || formIsLoading}
            disabled={loading || formIsLoading}
            onClick={handleDonate}
          >
            {t('Test donation')}
          </Button>
        </When>
      </Portal>
    </VStack>
  );
};

export default TestPanel;
